import { AlertTriangle, CheckCircle2, Loader2, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useConfirm } from "@/lib/confirm";

export function DemoDataSection() {
  const confirm = useConfirm();
  const utils = trpc.useUtils();
  const statusQuery = trpc.demo.status.useQuery();
  // Invalida TODAS as queries ativas numa tacada só: agenda (appointments
  // por range), pacientes (list), cadastros (partner list/counts/invites),
  // status do batch — tudo re-fetcha automaticamente. React Query dedupe
  // requests idênticos, então mesmo com N abas abertas o custo é baixo.
  const invalidateAll = () => utils.invalidate();

  const seed = trpc.demo.seed.useMutation({
    onSuccess: ({ counts }) => {
      toast.success(
        `Dados criados: ${counts.patients} pacientes, ${counts.appointments} consultas, ${counts.partners} cadastros.`,
      );
      invalidateAll();
    },
    onError: (err) =>
      toast.error(err.message || "Não foi possível criar dados de exemplo."),
  });
  const clear = trpc.demo.clear.useMutation({
    onSuccess: () => {
      toast.success("Dados de exemplo removidos.");
      invalidateAll();
    },
    onError: (err) => toast.error(err.message || "Erro ao remover."),
  });

  const handleSeed = async () => {
    const ok = await confirm({
      title: "Carregar dados de exemplo?",
      description:
        "Vai criar 40 pacientes, 150 consultas e 12 cadastros fictícios na sua clínica pra você testar. Você pode remover tudo depois a qualquer momento.",
      confirmText: "Carregar",
      cancelText: "Cancelar",
    });
    if (ok) seed.mutate();
  };

  const handleClear = async () => {
    const ok = await confirm({
      title: "Remover dados de exemplo?",
      description:
        "Todos os pacientes, consultas e cadastros marcados como demo serão apagados. Seus dados reais não são afetados.",
      confirmText: "Remover",
      cancelText: "Cancelar",
      destructive: true,
    });
    if (ok) clear.mutate();
  };

  const hasDemo = statusQuery.data?.hasDemoData;
  const counts = statusQuery.data?.counts;
  const isLoading = seed.isPending || clear.isPending;

  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-border dark:bg-card">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand-100 to-brand-50 text-brand-600 dark:from-primary/20 dark:to-primary/10 dark:text-primary">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">
            Dados de exemplo
          </h3>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-muted-foreground">
            Popule a clínica com pacientes, consultas e cadastros fictícios pra
            explorar a plataforma. Pode remover tudo quando quiser.
          </p>
        </div>
      </div>

      {statusQuery.isLoading ? (
        <div className="mt-4 flex justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
        </div>
      ) : hasDemo && counts ? (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2 rounded-lg bg-emerald-50 p-3 dark:bg-emerald-500/10">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <span className="text-[13px] font-medium text-emerald-900 dark:text-emerald-300">
              Dados de exemplo carregados
            </span>
            <span className="text-[11px] text-emerald-700 dark:text-emerald-400">
              · {counts.patients} pacientes · {counts.appointments} consultas ·{" "}
              {counts.partners} cadastros
            </span>
          </div>
          <button
            type="button"
            onClick={handleClear}
            disabled={isLoading}
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-red-200 px-3 text-[13px] font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60 dark:border-red-500/30 dark:hover:bg-red-500/10"
          >
            {clear.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            Remover dados de exemplo
          </button>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="text-[11px] leading-relaxed">
              Ao carregar, 40 pacientes, 150 consultas e 12 cadastros serão
              adicionados à sua clínica. Os dados são fictícios e ficam misturados
              com os seus — identificáveis por um marcador interno pra limpeza
              em um clique.
            </div>
          </div>
          <button
            type="button"
            onClick={handleSeed}
            disabled={isLoading}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-60"
          >
            {seed.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            Carregar dados de exemplo
          </button>
        </div>
      )}
    </section>
  );
}
